import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface TranslationProgress {
  englishCount: number;
  spanishCount: number;
  remainingCount: number;
  percentComplete: number;
  shouldResume: boolean;
}

export const useTranslationProgress = (courseId?: string, batchSize: number = 25, enablePolling: boolean = false) => {
  return useQuery({
    queryKey: ['translation-progress', courseId],
    queryFn: async (): Promise<TranslationProgress> => {
      console.log('Fetching translation progress for course:', courseId);
      
      // Build query for English questions
      let englishQuery = supabase
        .from('exam_questions')
        .select('id', { count: 'exact', head: true })
        .eq('language', 'English');
      
      if (courseId) {
        englishQuery = englishQuery.eq('course_id', courseId);
      }

      // Build query for Spanish questions
      let spanishQuery = supabase
        .from('exam_questions')
        .select('id', { count: 'exact', head: true })
        .eq('language', 'Spanish');
      
      if (courseId) {
        spanishQuery = spanishQuery.eq('course_id', courseId);
      }

      const [englishResult, spanishResult] = await Promise.all([
        englishQuery,
        spanishQuery
      ]);

      if (englishResult.error) {
        console.error('Error fetching English questions count:', englishResult.error);
        throw new Error(`Failed to fetch English questions: ${englishResult.error.message}`);
      }

      if (spanishResult.error) {
        console.error('Error fetching Spanish questions count:', spanishResult.error);
        throw new Error(`Failed to fetch Spanish questions: ${spanishResult.error.message}`);
      }

      const englishCount = englishResult.count || 0;
      const spanishCount = spanishResult.count || 0;
      const remainingCount = Math.max(0, englishCount - spanishCount);
      const percentComplete = englishCount > 0 ? Math.round((spanishCount / englishCount) * 100) : 0;
      const shouldResume = spanishCount > 0 && remainingCount > 0;

      console.log('Translation progress:', {
        englishCount,
        spanishCount,
        remainingCount,
        percentComplete,
        shouldResume
      });

      return {
        englishCount,
        spanishCount,
        remainingCount,
        percentComplete,
        shouldResume
      };
    },
    enabled: true,
    refetchOnWindowFocus: false,
    staleTime: 5000, // 5 seconds for fresher data
    refetchInterval: enablePolling ? 3000 : false, // Poll every 3 seconds during translation
  });
};
